// The single axios instance every feature talks through.
//
// Every backend sits behind the same nginx proxy in every environment, so all
// URLs here are relative and there is no base URL to configure.

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  getAccessToken,
  getRefreshToken,
  isRemembered,
  saveToken,
  clearToken,
} from "../auth/session";
import { refresh as refreshGrant } from "../auth/login";

/** Routes that must never carry a Bearer header. */
export const UNAUTHENTICATED_ROUTES: readonly (string | RegExp)[] = [
  "/api/v2/auth/oauth/login/",
  "/api/v2/auth/oauth/refresh/",
  "/api/auth/oauth/token",
  "oauth/token",
  /\/api\/auth\/otps\//,
  "/api/auth/oauth/reset-password",
  "/api/configurations/captchaSiteKey",
  "/api/v2/set-csrf-token/",
];

export function isUnauthenticatedRoute(url: string): boolean {
  return UNAUTHENTICATED_ROUTES.some((route) =>
    typeof route === "string" ? route === url : route.test(url),
  );
}

/** Django rejects unsafe methods without the CSRF header. */
const UNSAFE = new Set(["post", "put", "patch", "delete"]);

function readCsrfCookie(): string | null {
  const parts = `; ${document.cookie}`.split("; csrftoken=");
  if (parts.length !== 2) return null;
  return parts.pop()?.split(";").shift() ?? null;
}

export interface ApiErrorBody {
  errorMessages?: string[];
  message?: string;
  error?: string;
  error_description?: string;
}

/**
 * The user-facing message for a failed request: `errorMessages` joined by
 * newline (the Java services), then `message`, then `error_description`.
 */
export function errorMessageFor(error: unknown): string | null {
  if (!(error instanceof AxiosError)) return null;
  const body = error.response?.data as ApiErrorBody | undefined;
  if (!body) return null;
  if (Array.isArray(body.errorMessages) && body.errorMessages.length > 0) {
    return body.errorMessages.join("\n");
  }
  return body.message ?? body.error_description ?? null;
}

/** Statuses that get a toast; anything else fails silently to the caller. */
const TOASTED_STATUSES = new Set([400, 409, 417, 422, 500]);

export function shouldToast(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const status = error.response?.status;
  return status !== undefined && TOASTED_STATUSES.has(status);
}

type Notifier = (message: string) => void;
type Redirector = () => void;

let notify: Notifier = () => {};
let onSessionLost: Redirector = () => {};

/**
 * Wired up once at app start. Setters rather than imports, so this module
 * stays free of React and is testable on its own.
 */
export function configureClient(options: {
  notify?: Notifier;
  onSessionLost?: Redirector;
  /** Refresh-on-401 queue; on unless switched off. See the note below. */
  refreshEnabled?: boolean;
}): void {
  if (options.notify) notify = options.notify;
  if (options.onSessionLost) onSessionLost = options.onSessionLost;
  if (options.refreshEnabled !== undefined) refreshEnabled = options.refreshEnabled;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  /** Set once a request has already been replayed after a refresh. */
  _retried?: boolean;
  /** Opt a single call out of the error toast. */
  silent?: boolean;
}

export const api: AxiosInstance = axios.create({
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const url = config.url ?? "";
  if (!isUnauthenticatedRoute(url)) {
    const token = getAccessToken();
    if (token) config.headers.set("Authorization", `Bearer ${token}`);
  }
  // Django's session-authenticated endpoints need the CSRF header; the Java
  // services ignore it.
  if (url.startsWith("/api/v2/") && UNSAFE.has((config.method ?? "get").toLowerCase())) {
    const csrf = readCsrfCookie();
    if (csrf) config.headers.set("X-CSRFToken", csrf);
  }
  return config;
});

// --- single-flight refresh -------------------------------------------------
//
// Concurrent 401s wait on ONE refresh call, then replay. Access tokens live
// an hour and refresh tokens 30 days, so a 401 usually means "refresh", not
// "the session is over". `configureClient({ refreshEnabled: false })` makes
// every 401 a logout instead.
let refreshEnabled = true;

let refreshInFlight: Promise<string> | null = null;

async function runRefresh(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("no refresh token");
  let token;
  try {
    token = await refreshGrant(refreshToken);
  } catch (error) {
    // Tabs share the cookies but not this single-flight lock. When another
    // tab spent the same refresh token first, its new pair is already saved.
    const access = getAccessToken();
    if (getRefreshToken() !== refreshToken && access) return access;
    throw error;
  }
  // The server revokes the old pair, so every store must move to the new one.
  saveToken(token, isRemembered());
  return token.access_token;
}

function refreshOnce(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = runRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && config && !config._retried && !isUnauthenticatedRoute(config.url ?? "")) {
      config._retried = true;
      if (!refreshEnabled) {
        clearToken();
        onSessionLost();
        return Promise.reject(error);
      }
      try {
        const token = await refreshOnce();
        config.headers.set("Authorization", `Bearer ${token}`);
        return await api.request(config);
      } catch {
        clearToken();
        onSessionLost();
        return Promise.reject(error);
      }
    }

    if (!config?.silent && shouldToast(error)) {
      const message = errorMessageFor(error);
      if (message) notify(message);
    }
    return Promise.reject(error);
  },
);

/** Test seam -- drops any in-flight refresh so cases do not leak into each other. */
export function resetRefreshState(): void {
  refreshInFlight = null;
}

export type { AxiosRequestConfig };
